import { useState, useEffect, useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { MapPin, Calendar, Sparkles } from "lucide-react";
import { useLang } from "@/hooks/use-lang";
import { usePremium } from "@/hooks/usePremium";
import PremiumPaywall from "@/components/PremiumPaywall";

interface TripResults {
  city: string;
  dates: string;
  style: string;
  activities: { name: string; description: string; icon: string }[];
  hotels: { name: string; description: string; price: string; category?: string }[];
  restaurants: { name: string; description: string; type: string; price_level?: string }[];
}

interface ResultsPanelProps {
  results: TripResults | null;
}

type HotelFilter = "all" | "budget" | "standard" | "premium";
type RestaurantFilter = "all" | "local" | "street_food" | "fine_dining";

const ResultsPanel = ({ results }: ResultsPanelProps) => {
  const { t, lang } = useLang();
  const { isPremium } = usePremium();
  const [visibleSections, setVisibleSections] = useState<number>(0);
  const [hotelFilter, setHotelFilter] = useState<HotelFilter>("all");
  const [restaurantFilter, setRestaurantFilter] = useState<RestaurantFilter>("all");
  const prevResultsRef = useRef<TripResults | null>(null);

  // Sequential reveal: summary → activities → hotels → restaurants
  useEffect(() => {
    if (!results) {
      setVisibleSections(0);
      prevResultsRef.current = null;
      return;
    }
    // Only animate on new results
    if (results !== prevResultsRef.current) {
      prevResultsRef.current = results;
      setVisibleSections(0);
      const timers: ReturnType<typeof setTimeout>[] = [];
      for (let i = 1; i <= 4; i++) {
        timers.push(setTimeout(() => setVisibleSections(i), i * 300));
      }
      return () => timers.forEach(clearTimeout);
    }
  }, [results]);

  if (!results) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center px-8">
        <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
          <Sparkles className="w-8 h-8 text-primary" />
        </div>
        <p className="text-muted-foreground text-sm max-w-[250px]">{t.noResults}</p>
      </div>
    );
  }

  return (
    <ScrollArea className="h-full">
      <div className="p-5 space-y-8">
        {/* Summary */}
        <div className={`transition-all duration-500 ease-out ${visibleSections >= 1 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <Card className="bg-primary/5 border-primary/20 shadow-card rounded-2xl">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <MapPin className="w-4 h-4 text-primary" />
                <span className="font-bold text-foreground text-lg">{results.city}</span>
              </div>
              <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5" />
                  {results.dates}
                </span>
                <Badge variant="secondary" className="text-xs">{results.style}</Badge>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Activities */}
        <div className={`transition-all duration-500 ease-out ${visibleSections >= 2 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <section>
            <h3 className="text-lg font-bold text-foreground mb-3">{t.activities}</h3>
            <div className="space-y-2.5">
              {results.activities.map((a, i) => (
                <Card key={i} className="shadow-card hover:shadow-card-hover hover:-translate-y-0.5 transition-all duration-200 rounded-2xl">
                  <CardContent className="p-4 flex items-start gap-3">
                    <span className="text-xl shrink-0 mt-0.5">{a.icon}</span>
                    <div>
                      <h4 className="font-semibold text-foreground text-sm">{a.name}</h4>
                      <p className="text-xs text-muted-foreground mt-0.5">{a.description}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        </div>

        {/* Paywall or Hotels+Restaurants */}
        {!isPremium ? (
          <div className={`transition-all duration-500 ease-out ${visibleSections >= 3 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
            <PremiumPaywall variant="overlay" />
          </div>
        ) : (
        <>
        {/* Hotels with filter */}
        <div className={`transition-all duration-500 ease-out ${visibleSections >= 3 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <section>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold text-foreground">{t.hotels}</h3>
            </div>
            
            {/* Filter tabs */}
            <div className="flex items-center gap-2 mb-4 overflow-x-auto no-scrollbar">
              {[
                { key: "all" as HotelFilter, label: lang === "fr" ? "Tous" : "All", icon: "🔍" },
                { key: "budget" as HotelFilter, label: lang === "fr" ? "Budget" : "Budget", icon: "🏠" },
                { key: "standard" as HotelFilter, label: "Standard", icon: "🏨" },
                { key: "premium" as HotelFilter, label: "Premium", icon: "✨" },
              ].map(({ key, label, icon }) => {
                const isActive = hotelFilter === key;
                return (
                  <button
                    key={key}
                    onClick={() => setHotelFilter(key)}
                    className={`
                      flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold
                      transition-all duration-200 border whitespace-nowrap shrink-0
                      ${isActive
                        ? "bg-primary text-primary-foreground border-primary shadow-md scale-105"
                        : "bg-card/80 text-foreground/80 border-border/40 hover:bg-card hover:border-border/60"
                      }
                    `}
                  >
                    <span>{icon}</span>
                    <span>{label}</span>
                  </button>
                );
              })}
            </div>

            <div className="space-y-2.5">
              {(["budget", "standard", "premium"] as const)
                .filter((cat) => hotelFilter === "all" || hotelFilter === cat)
                .map((cat) => {
                const hotel = results.hotels.find((h) => h.category === cat) 
                  || (cat === "budget" ? results.hotels[0] : cat === "standard" ? results.hotels[1] : results.hotels[2]);
                if (!hotel) return null;
                const categoryConfig = {
                  budget: { 
                    label: lang === "fr" ? "Budget" : "Budget", 
                    icon: "🏠", 
                    badgeClass: "bg-green-500/15 text-green-700 border-green-500/30 dark:text-green-400" 
                  },
                  standard: { 
                    label: "Standard", 
                    icon: "🏨", 
                    badgeClass: "bg-blue-500/15 text-blue-700 border-blue-500/30 dark:text-blue-400" 
                  },
                  premium: { 
                    label: "Premium", 
                    icon: "✨", 
                    badgeClass: "bg-amber-500/15 text-amber-700 border-amber-500/30 dark:text-amber-400" 
                  },
                };
                const config = categoryConfig[cat];
                return (
                  <Card key={cat} className="shadow-card hover:shadow-card-hover hover:-translate-y-0.5 transition-all duration-200 rounded-2xl">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-base">{config.icon}</span>
                            <Badge className={`text-[10px] font-bold border ${config.badgeClass}`}>
                              {config.label}
                            </Badge>
                          </div>
                          <h4 className="font-semibold text-foreground text-sm">{hotel.name}</h4>
                          <p className="text-xs text-muted-foreground mt-0.5">{hotel.description}</p>
                        </div>
                        <Badge variant="secondary" className="text-xs font-bold whitespace-nowrap shrink-0 mt-1">
                          {hotel.price}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>
        </div>

        {/* Restaurants */}
        <div className={`transition-all duration-500 ease-out ${visibleSections >= 4 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <section>
            <h3 className="text-lg font-bold text-foreground mb-3">{t.food}</h3>

            {/* Restaurant filter tabs */}
            <div className="flex items-center gap-2 mb-4 overflow-x-auto no-scrollbar">
              {([
                { key: "all" as RestaurantFilter, label: lang === "fr" ? "Tous" : "All", icon: "🔍" },
                { key: "local" as RestaurantFilter, label: lang === "fr" ? "Local" : "Local", icon: "🏛" },
                { key: "street_food" as RestaurantFilter, label: "Street food", icon: "🍜" },
                { key: "fine_dining" as RestaurantFilter, label: lang === "fr" ? "Gastro" : "Fine dining", icon: "⭐" },
              ]).map(({ key, label, icon }) => {
                const isActive = restaurantFilter === key;
                return (
                  <button
                    key={key}
                    onClick={() => setRestaurantFilter(key)}
                    className={`
                      flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold
                      transition-all duration-200 border whitespace-nowrap shrink-0
                      ${isActive
                        ? "bg-primary text-primary-foreground border-primary shadow-md scale-105"
                        : "bg-card/80 text-foreground/80 border-border/40 hover:bg-card hover:border-border/60"
                      }
                    `}
                  >
                    <span>{icon}</span>
                    <span>{label}</span>
                  </button>
                );
              })}
            </div>

            <div className="space-y-2.5">
              {results.restaurants
                .filter((r) => {
                  if (restaurantFilter === "all") return true;
                  // Support legacy types
                  if (r.type === "traditionnel" || r.type === "traditional") return restaurantFilter === "local";
                  if (r.type === "tendance" || r.type === "trendy") return restaurantFilter === "fine_dining";
                  return r.type === restaurantFilter;
                })
                .map((r, i) => {
                const categoryConfig: Record<string, { label: string; icon: string; badgeClass: string }> = {
                  local: { label: lang === "fr" ? "Local" : "Local", icon: "🏛", badgeClass: "bg-green-500/15 text-green-700 border-green-500/30 dark:text-green-400" },
                  street_food: { label: "Street food", icon: "🍜", badgeClass: "bg-orange-500/15 text-orange-700 border-orange-500/30 dark:text-orange-400" },
                  fine_dining: { label: lang === "fr" ? "Gastro" : "Fine dining", icon: "⭐", badgeClass: "bg-purple-500/15 text-purple-700 border-purple-500/30 dark:text-purple-400" },
                  // Legacy mappings
                  traditionnel: { label: lang === "fr" ? "Local" : "Local", icon: "🏛", badgeClass: "bg-green-500/15 text-green-700 border-green-500/30 dark:text-green-400" },
                  traditional: { label: "Local", icon: "🏛", badgeClass: "bg-green-500/15 text-green-700 border-green-500/30 dark:text-green-400" },
                  tendance: { label: lang === "fr" ? "Gastro" : "Fine dining", icon: "⭐", badgeClass: "bg-purple-500/15 text-purple-700 border-purple-500/30 dark:text-purple-400" },
                  trendy: { label: "Fine dining", icon: "⭐", badgeClass: "bg-purple-500/15 text-purple-700 border-purple-500/30 dark:text-purple-400" },
                };
                const config = categoryConfig[r.type] ?? categoryConfig.local;
                return (
                  <Card key={i} className="shadow-card hover:shadow-card-hover hover:-translate-y-0.5 transition-all duration-200 rounded-2xl">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-base">{config.icon}</span>
                            <Badge className={`text-[10px] font-bold border ${config.badgeClass}`}>
                              {config.label}
                            </Badge>
                          </div>
                          <h4 className="font-semibold text-foreground text-sm">{r.name}</h4>
                          <p className="text-xs text-muted-foreground mt-0.5">{r.description}</p>
                        </div>
                        {r.price_level && (
                          <Badge variant="secondary" className="text-xs font-bold whitespace-nowrap shrink-0 mt-1">
                            {r.price_level}
                          </Badge>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>
        </div>
        </>
        )}
      </div>
    </ScrollArea>
  );
};

export default ResultsPanel;
